<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            v-model.trim="searchTable"
            type="search"
            size="sm"
            class="field-input-native"
            placeholder="ชื่อส่วนลด"
          />
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>
    </div>

    <div
      id="report-elements"
      ref="fullscreenRoot"
      :class="{ 'my-fullscreen': isFullscreen }"
    >
      <div class="my-fullscreen-bar">
        <span class="title">{{ reportTitle }}</span>
        <div class="meta">
          <span v-if="dateRange[0] && dateRange[1]">
            วันที่: {{ formatDate(dateRange[0]) }} – {{ formatDate(dateRange[1]) }}
          </span>
          <span v-if="branchInfo && branchInfo.name">
            สาขา: {{ branchInfo.name }}
          </span>
        </div>
        <span class="spacer"></span>
        <b-button
          size="sm"
          variant="outline-secondary"
          :title="fullscreenLabel"
          :aria-label="fullscreenLabel"
          @click="toggleFullscreen"
        >
          <i class="fas fa-times"></i>
        </b-button>
      </div>

      <div class="toolbar">
        <div class="legend">
          <span><i class="sw sw-use"></i> การใช้งาน</span>
          <span><i class="sw sw-disc"></i> ส่วนลด</span>
          <span><i class="sw sw-rev"></i> รายได้</span>
        </div>
        <div class="toolbar-right">
          <b-dropdown
            size="sm"
            variant="outline-success"
            right
            :disabled="!filteredReportList.length || isExporting"
          >
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="exportExcel">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="exportPdf">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            size="sm"
            :variant="fullscreenButtonVariant"
            :title="fullscreenLabel"
            :aria-label="fullscreenLabel"
            @click="toggleFullscreen"
          >
            <i class="fas" :class="fullscreenIcon"></i>
          </b-button>
        </div>
      </div>

      <div class="table-responsive">
        <table
          id="discountUsageTable"
          class="table table-sm report-table discount-usage"
        >
          <thead class="thead-light">
            <tr>
              <th class="text-left">ส่วนลด</th>
              <th class="g-use text-right">จำนวนใบเสร็จ</th>
              <th class="g-use text-right">จำนวนรายการ</th>
              <th class="text-right">ยอดเต็ม</th>
              <th class="g-disc text-right">ส่วนลดรวม</th>
              <th class="g-disc text-right">%</th>
              <th class="g-disc text-right">ส่วนลดแพทย์</th>
              <th class="g-disc text-right">ส่วนลดคลินิก</th>
              <th class="g-rev text-right">รายได้</th>
              <th class="text-left">โควตา</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredReportList.length">
              <td colspan="10" class="text-center text-muted my-2 py-3">
                ไม่มีข้อมูลให้แสดง
              </td>
            </tr>
            <template v-for="row in filteredReportList" v-else>
              <tr
                :key="`row-${row.discountId}`"
                class="expandable"
                :class="{ open: expandedId === row.discountId }"
                @click="toggleRow(row)"
              >
                <td class="text-left" :class="{ 'text-muted font-italic': row.discountId === 0 }">
                  <span class="mr-1">{{ expandedId === row.discountId ? "▾" : "▸" }}</span>
                  {{ row.name }}
                  <b-badge v-if="row.scope === 'RECEIPT'" variant="info" class="ml-1">ทั้งใบเสร็จ</b-badge>
                  <b-badge v-if="row.isActive === 0" variant="danger" class="ml-1">ปิดใช้งาน</b-badge>
                </td>
                <td class="g-use text-right">{{ row.redemptions }}</td>
                <td class="g-use text-right">{{ row.lines }}</td>
                <td class="text-right">{{ formatNumber(row.gross) }}</td>
                <td class="g-disc text-right">{{ formatNumber(row.discount) }}</td>
                <td class="g-disc text-right">{{ percent(row.discountPct) }}</td>
                <td class="g-disc text-right">{{ formatNumber(row.doctorShare) }}</td>
                <td class="g-disc text-right">{{ formatNumber(row.clinicShare) }}</td>
                <td class="g-rev text-right">{{ formatNumber(row.net) }}</td>
                <td class="text-left">{{ quota(row) }}</td>
              </tr>
              <tr v-if="expandedId === row.discountId" :key="`lines-${row.discountId}`" class="lines-row report-export-skip">
                <td colspan="10" class="p-0">
                  <div class="lines-wrap">
                    <div v-if="linesBusy" class="text-center py-2">กำลังโหลดรายการ...</div>
                    <table v-else class="table table-sm mb-0 lines-table">
                      <thead>
                        <tr>
                          <th class="text-left">เลขที่ใบเสร็จ</th>
                          <th class="text-left">วันที่</th>
                          <th class="text-left">คนไข้</th>
                          <th class="text-left">การรักษา</th>
                          <th class="text-left">แพทย์</th>
                          <th class="text-right">ยอดเต็ม</th>
                          <th class="g-disc text-right">ส่วนลดรวม</th>
                          <th class="g-disc text-right">แพทย์</th>
                          <th class="g-disc text-right">คลินิก</th>
                          <th class="g-rev text-right">รายได้</th>
                          <th class="text-left">ผู้บันทึก</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="group in lineGroups">
                          <tr v-for="(line, i) in group.lines" :key="line.receiptItemId">
                            <td v-if="i === 0" :rowspan="group.lines.length" class="text-left align-top">
                              <a href="#" @click.prevent="openReceipt(line)">{{ line.receiptNo }}</a>
                            </td>
                            <td class="text-left">{{ formatDate(line.creationDt) }}</td>
                            <td class="text-left">{{ line.patientFullName }}</td>
                            <td class="text-left">{{ line.treatmentName }}</td>
                            <td class="text-left">{{ line.doctorFullName }}</td>
                            <td class="text-right">{{ formatNumber(line.gross) }}</td>
                            <td class="g-disc text-right">{{ formatNumber(line.discount) }}</td>
                            <td class="g-disc text-right">{{ formatNumber(line.doctorShare) }}</td>
                            <td class="g-disc text-right">{{ formatNumber(line.clinicShare) }}</td>
                            <td class="g-rev text-right">{{ formatNumber(line.net) }}</td>
                            <td class="text-left">{{ line.appliedByFullName }}</td>
                          </tr>
                          <tr :key="`sub-${group.receiptNo}`" class="receipt-subtotal">
                            <td colspan="5" class="text-left font-italic">
                              รวมใบเสร็จ · 1 ใบเสร็จ · {{ group.lines.length }} รายการ
                            </td>
                            <td class="text-right">{{ formatNumber(group.gross) }}</td>
                            <td class="text-right">{{ formatNumber(group.discount) }}</td>
                            <td class="text-right">{{ formatNumber(group.doctorShare) }}</td>
                            <td class="text-right">{{ formatNumber(group.clinicShare) }}</td>
                            <td class="text-right">{{ formatNumber(group.net) }}</td>
                            <td></td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot v-if="filteredReportList.length">
            <tr class="grand-total">
              <td class="text-left">รวมทั้งหมด</td>
              <td class="g-use text-right">{{ totals.redemptions }}</td>
              <td class="g-use text-right">{{ totals.lines }}</td>
              <td class="text-right">{{ formatNumber(totals.gross) }}</td>
              <td class="g-disc text-right">{{ formatNumber(totals.discount) }}</td>
              <td class="g-disc text-right">{{ percent(totals.discountPct) }}</td>
              <td class="g-disc text-right">{{ formatNumber(totals.doctorShare) }}</td>
              <td class="g-disc text-right">{{ formatNumber(totals.clinicShare) }}</td>
              <td class="g-rev text-right">{{ formatNumber(totals.net) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!--
      Off-screen source for the Excel "รายละเอียด" sheet. exportTableToExcel builds every sheet
      from a DOM table, and the on-screen drill-down cannot serve: it is stripped by
      report-export-skip and only ever holds ONE discount's lines. Rendered only while an
      export is in flight, then torn down.
      Positioned off-screen rather than display:none so the rows are unambiguously present.
    -->
    <div
      v-if="exportLines.length"
      class="export-source"
      aria-hidden="true"
    >
      <table id="discountUsageDetailTable" class="table table-sm">
        <thead>
          <tr>
            <th class="text-left">ส่วนลด</th>
            <th class="text-left">เลขที่ใบเสร็จ</th>
            <th class="text-left">วันที่</th>
            <th class="text-left">คนไข้</th>
            <th class="text-left">การรักษา</th>
            <th class="text-left">แพทย์</th>
            <th class="text-right">ยอดเต็ม</th>
            <th class="text-right">ส่วนลดรวม</th>
            <th class="text-right">ส่วนลดแพทย์</th>
            <th class="text-right">ส่วนลดคลินิก</th>
            <th class="text-right">รายได้</th>
            <th class="text-left">ผู้บันทึก</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in exportLines" :key="`x-${line.scope}-${line.receiptItemId}`">
            <td class="text-left">{{ exportDiscountName(line) }}</td>
            <td class="text-left">{{ line.receiptNo }}</td>
            <td class="text-left">{{ formatDate(line.creationDt) }}</td>
            <td class="text-left">{{ line.patientFullName }}</td>
            <td class="text-left">{{ line.treatmentName }}</td>
            <td class="text-left">{{ line.doctorFullName }}</td>
            <td class="text-right">{{ formatNumber(line.gross) }}</td>
            <td class="text-right">{{ formatNumber(line.discount) }}</td>
            <td class="text-right">{{ formatNumber(line.doctorShare) }}</td>
            <td class="text-right">{{ formatNumber(line.clinicShare) }}</td>
            <td class="text-right">{{ formatNumber(line.net) }}</td>
            <td class="text-left">{{ line.appliedByFullName }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";
import { matchesSearchQuery } from "@/utils/textSearch";
import reportFullscreenMixin from "@/mixins/reportFullscreenMixin";
import { eventBus } from "@/main";

export default {
  name: "DiscountUsageReport",
  mixins: [reportFullscreenMixin],
  components: { Dialog, Loading },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานส่วนลด",
      reportNameEn: "DiscountUsage",

      dateRange: [],
      searchTable: "",

      linesBusy: false,
      reportList: [],
      expandedId: null,
      lines: [],
      exportLines: [],
      isExporting: false,
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    filteredReportList() {
      return this.reportList.filter(r =>
        matchesSearchQuery(r, this.searchTable, ["name"])
      );
    },
    totals() {
      const sum = key =>
        this.filteredReportList.reduce((s, r) => s + (r[key] || 0), 0);
      const gross = sum("gross");
      const discount = sum("discount");
      return {
        redemptions: sum("redemptions"),
        lines: sum("lines"),
        gross,
        discount,
        discountPct: gross === 0 ? 0 : discount / gross,
        net: sum("net"),
        doctorShare: sum("doctorShare"),
        clinicShare: sum("clinicShare"),
      };
    },
    // The API returns flat rows ordered by receiptNo; group them client-side so a
    // receipt-scoped discount reads as "1 ใบเสร็จ, N รายการ".
    lineGroups() {
      const groups = [];
      let current = null;
      for (const line of this.lines) {
        if (!current || current.receiptNo !== line.receiptNo) {
          current = {
            receiptNo: line.receiptNo,
            lines: [],
            gross: 0,
            discount: 0,
            doctorShare: 0,
            clinicShare: 0,
            net: 0,
          };
          groups.push(current);
        }
        current.lines.push(line);
        current.gross += line.gross;
        current.discount += line.discount;
        current.doctorShare += line.doctorShare;
        current.clinicShare += line.clinicShare;
        current.net += line.net;
      }
      return groups;
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
  },
  methods: {
    ...mapActions({
      fetchDiscountUsageReport: "moduleReport/fetchDiscountUsageReport",
      fetchDiscountUsageReportLines: "moduleReport/fetchDiscountUsageReportLines",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    formatNumber(v) {
      return Number(v || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    percent(v) {
      return `${(Number(v || 0) * 100).toFixed(1)}%`;
    },
    quota(row) {
      if (row.discountId === 0) return "—";
      if (row.maxUsage === null || row.maxUsage === undefined) return "ไม่จำกัด";
      return `${row.usageCount} / ${row.maxUsage}`;
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    rangeParams() {
      return {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        startDt: moment(this.dateRange[0]).startOf("day").toISOString(),
        // endDt is exclusive: the day after dateTo at 00:00
        endDt: moment(this.dateRange[1]).add(1, "day").startOf("day").toISOString(),
      };
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.expandedId = null;
      this.lines = [];
      try {
        const res = await this.fetchDiscountUsageReport(this.rangeParams());
        this.reportList = res.data.report || [];
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายงานส่วนลดได้");
      } finally {
        this.isLoading = false;
      }
    },
    async toggleRow(row) {
      if (this.expandedId === row.discountId) {
        this.expandedId = null;
        this.lines = [];
        return;
      }
      const reqId = row.discountId;
      this.expandedId = reqId;
      this.lines = [];
      this.linesBusy = true;
      try {
        const res = await this.fetchDiscountUsageReportLines({
          ...this.rangeParams(),
          discountId: reqId,
        });
        if (this.expandedId !== reqId) return;
        this.lines = res.data.report || [];
        this.linesBusy = false;
      } catch (err) {
        if (this.expandedId !== reqId) return;
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายการส่วนลดได้");
        this.expandedId = null;
        this.linesBusy = false;
      }
    },
    openReceipt(line) {
      eventBus.$emit("openReceipt", { id: line.receiptId, patientId: line.patientId });
    },
    exportOptions() {
      return {
        tableId: "discountUsageTable",
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    // A stacked line appears twice in the detail feed — once for its item discount, once
    // for the voucher slice — so each row has to name its own discount. The on-screen
    // drill-down does not need this: there, the expanded row already says which one it is.
    exportDiscountName(line) {
      const name = line.discountName || "";
      return line.scope === "RECEIPT" ? `${name} (ทั้งใบเสร็จ)` : name;
    },
    async exportExcel() {
      if (this.isExporting) return;
      this.isExporting = true;
      this.isLoading = true;
      try {
        // No discountId: the API returns every discounted line in the range, item
        // portions and voucher portions alike.
        const res = await this.fetchDiscountUsageReportLines(this.rangeParams());
        this.exportLines = res.data.report || [];
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายละเอียดส่วนลดได้");
        this.isExporting = false;
        this.isLoading = false;
        return;
      }

      try {
        await this.$nextTick();
        exportTableToExcel({
          ...this.exportOptions(),
          sheets: [
            { name: "สรุป", tableId: "discountUsageTable" },
            {
              name: "รายละเอียด",
              tableId: "discountUsageDetailTable",
              reportTitle: `${this.reportTitle} — รายละเอียดรายใบเสร็จ`,
            },
          ],
        });
      } catch (err) {
        this.$refs.Dialog.showToast("danger", "ไม่สามารถส่งออก Excel ได้");
      } finally {
        this.exportLines = [];
        this.isExporting = false;
        this.isLoading = false;
      }
    },
    // Summary only, deliberately: the detail feed runs to thousands of rows in a busy
    // month, which is not a PDF anyone reads. Use Excel for line detail.
    exportPdf() {
      exportReportToPdf(this.exportOptions());
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped>
.legend {
  display: flex;
  gap: 14px;
  font-size: 0.75rem;
}
.legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.sw {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.sw-use { background: #e7f1ff; }
.sw-disc { background: #fff2d6; }
.sw-rev { background: #e3f6e8; }

.discount-usage th.g-use { background: #e7f1ff; }
.discount-usage td.g-use { background: #f3f8ff; }
.discount-usage th.g-disc { background: #fff2d6; }
.discount-usage td.g-disc { background: #fffaf0; }
.discount-usage th.g-rev { background: #e3f6e8; }
.discount-usage td.g-rev { background: #f4fbf6; }

.discount-usage tr.expandable { cursor: pointer; }
.discount-usage tr.expandable:hover td { background: #f1f7ff; }
.discount-usage tr.open td { background: #eaf3ff; font-weight: 600; }
.discount-usage tfoot tr.grand-total td {
  border-top: 2px solid #adb5bd;
  background: #f8f9fa;
  font-weight: 700;
}
.export-source {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.lines-wrap { padding: 8px 8px 10px 28px; background: #fbfcfd; }
.lines-table tr.receipt-subtotal td {
  background: #f1f3f5;
  font-weight: 600;
  border-bottom: 1px solid #ced4da;
}
</style>
